import React, { useRef, useState } from "react";
import { useTheme } from '../../context/ThemeContext';
import * as mammoth from "mammoth";
import { toJpeg } from "html-to-image";
import { FiUpload, FiDownload, FiFileText, FiImage } from "react-icons/fi";
import { FaRegFileWord } from "react-icons/fa";
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImage } from "@fortawesome/free-solid-svg-icons";


export default function WordToJpgConverter() {
  const [docHtml, setDocHtml] = useState("");
  const { darkMode } = useTheme();
  const [imageUrl, setImageUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const displayRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword"
    ];

    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith(".docx")) {
      alert("Please upload a valid Word document (.docx)");
      return;
    }

    try {
      setIsConverting(true);
      setFileName(file.name);
      setImageUrl(null);
      setProgress(30); // Initial progress

      const arrayBuffer = await file.arrayBuffer();
      setProgress(50);
      
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setProgress(70);
      
      const cleanedHtml = `
        <div style="
          font-family: 'Segoe UI', Arial, sans-serif;
          line-height: 1.6;
          padding: 2rem;
          max-width: 800px;
          margin: 0 auto;
          white-space: pre-wrap;
          color: #333;
          font-size: 1rem;
        ">
          ${result.value}
        </div>
      `;
      
      setDocHtml(cleanedHtml);
      setProgress(90);
      
      // Small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      setProgress(100);
    } catch (error) {
      console.error("Conversion error:", error);
      alert("Failed to process the document. Please try again.");
    } finally {
      setIsConverting(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const handleConvertToImage = async () => {
    if (!displayRef.current) return;

    try {
      setIsConverting(true);
      setProgress(0);
      
      const dataUrl = await toJpeg(displayRef.current, {
        quality: 0.95,
        canvasWidth: displayRef.current.scrollWidth * 2,
        canvasHeight: displayRef.current.scrollHeight * 2,
        backgroundColor: "#ffffff"
      });
      
      setImageUrl(dataUrl);
      setProgress(100);
    } catch (err) {
      console.error("Image conversion error:", err);
      alert("Failed to generate image. Please try again.");
    } finally {
      setIsConverting(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = fileName 
      ? `${fileName.replace(/\.[^/.]+$/, "")}.jpg` 
      : "converted-document.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = null; // Reset input
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
    <HeaderSection toolId="wordToJpg" />
    <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
      <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faFileImage}
                                className="mr-3"
                            />
                            Word to JPG Converter
                        </h1>
                        <p className="opacity-90">
                            Convert your Word documents to high-quality JPG images instantly
                        </p>
                    </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Upload Section */}
          <div 
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={triggerFileInput}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer mb-6
              ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-green-400"}`}
          >
            <div className="flex flex-col items-center justify-center gap-4">
              <FiUpload className="text-4xl text-blue-500" />
              <div>
                <p className="font-medium text-lg dark:text-white ">
                  {isDragging ? "Drop your file here" : "Drag & drop your Word file here"}
                </p>
                <p className="text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">or</p>
              </div>
              <button
                type="button"
                className="px-6 py-2 bg-[#74D19C] text-white rounded-full hover:bg-blue-700 transition flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <FiFileText /> Select File
              </button>
              <p className="text-sm text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-2">
                Supports .docx files (Max 10MB)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleInputChange}
              className="hidden"
              disabled={isConverting}
            />
          </div>

          {/* Progress Bar */}
          {isConverting && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 mb-1">
                <span>Converting...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* File Info */}
          {fileName && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-center gap-3">
              <FiFileText className="text-blue-600 text-xl" />
              <div>
                <p className="font-medium">{fileName}</p>
                <p className="text-sm text-gray-600">
                  {isConverting ? "Processing..." : "Ready for conversion"}
                </p>
              </div>
            </div>
          )}

          {/* Document Preview */}
          {docHtml && !isConverting && (
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FiFileText /> Document Preview
                </h2>
                <button
                  onClick={handleConvertToImage}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <FiImage /> Convert to JPG
                </button>
              </div>
              <div
                ref={displayRef}
                className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 overflow-auto max-h-[500px]"
                dangerouslySetInnerHTML={{ __html: docHtml }}
              />
            </div>
          )}

          {/* Result Section */}
          {imageUrl && (
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <h2 className="text-xl font-semibold text-green-800 flex items-center gap-2">
                  <FiImage /> Conversion Successful!
                </h2>
                <button
                  onClick={handleDownload}
                  className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                >
                  <FiDownload /> Download JPG
                </button>
              </div>
              
              <div className="flex justify-center">
                <div className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 inline-block">
                  <img
                    src={imageUrl}
                    alt="Converted JPG"
                    className="max-w-full h-auto max-h-[400px]"
                  />
                </div>
              </div>
              
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 text-center">
                <p>Image saved as: <span className="font-medium">{fileName.replace(/\.[^/.]+$/, "")}.jpg</span></p>
              </div>
            </div>
          )}

          {/* Instructions */}
          {!docHtml && !isConverting && (
            <div className="mt-8 dark:text-white bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-6 rounded-lg border border-gray-200">
              <h3 className="font-medium text-lg mb-3 text-center">How to convert Word to JPG:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-white">
                <li>Click "Select File" or drag and drop your Word document</li>
                <li>Preview your document content</li>
                <li>Click "Convert to JPG" button</li>
                <li>Download your JPG image</li>
              </ol>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100   dark:bg-gray-800  dark:text-white p-4 text-center text-sm text-gray-600">
          <p>Convert Word documents to high-quality JPG images for free</p>
        </div>
      </div>
    </div>
    <BottomSection toolId="wordToJpg" />
    </>
  );
}